// apps/web/src/components/BookingOpportunitiesPanel.tsx
// Opportunity lens for Territory / Pipeline — deep-links into existing modules.
// Phase 2: Top-20 campaigns + instrumented EPK engagement + decline reasons.

import { useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { ApiClientError } from '@/lib/api-client'
import { artistFetch } from '@/lib/artist-api'
import { AsyncBoundary } from '@/components/AsyncBoundary'
import { cn } from '@/lib/utils'

interface EvidenceCard {
  id: string
  source: string
  claim: string
  confidenceGrade: string
  weight: number
}

interface OpportunityRow {
  id: string
  title: string
  status: string
  marketChannel: string
  city: string | null
  country: string | null
  buyerOrgName: string | null
  venueName: string | null
  artistMarketFitScore: number | null
  opportunityScore: number | null
  evidenceGrade: string | null
  evidence: EvidenceCard[] | null
  recommendedEpkVariant: string | null
  declineReason: string | null
  inquiry: { id: string; stage: string } | null
  epkLink: {
    id: string
    token: string
    views: number
    lastViewedAt: string | null
    variant: string
  } | null
}

interface OpportunitiesPayload {
  items: OpportunityRow[]
  total: number
}

interface ChannelRow {
  marketChannel: string
  recommendedEpkVariant: string
  opportunityCount: number
  avgOpportunityScore: number | null
}

interface CampaignSummary {
  id: string
  name: string
  status: string
  marketChannel: string | null
  _count: { targets: number }
  targets: Array<{
    rank: number
    opportunity: { id: string; title: string; opportunityScore: number | null }
  }>
}

interface CampaignDetail {
  id: string
  name: string
  status: string
  marketChannel: string | null
  targets: Array<{
    id: string
    rank: number
    outreachStatus: string
    epkLink: {
      id: string
      token: string
      views: number
      lastViewedAt: string | null
      variant: string
    } | null
    opportunity: {
      id: string
      title: string
      status: string
      marketChannel: string
      buyerOrgName: string | null
      opportunityScore: number | null
      recommendedEpkVariant: string | null
    }
  }>
}

interface EngagementRow {
  linkId: string
  label: string | null
  views: number
  docxDownloads: number
  lastViewedAt: string | null
  opportunities: Array<{ id: string; title: string }>
  campaigns: Array<{ campaignName: string; opportunityTitle: string }>
}

const CHANNELS = [
  'all',
  'pac_theatre',
  'casino',
  'festival',
  'corporate_gala',
  'destination',
  'international',
  'private',
  'media_sync',
] as const

function label(s: string) {
  return s.replace(/_/g, ' ')
}

export function BookingOpportunitiesPanel({
  compact = false,
}: {
  compact?: boolean
}) {
  const qc = useQueryClient()
  const [channel, setChannel] = useState<string>('all')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [title, setTitle] = useState('')
  const [newChannel, setNewChannel] = useState('pac_theatre')
  const [campaignName, setCampaignName] = useState('')
  const [creatingCampaign, setCreatingCampaign] = useState(false)
  const [openCampaignId, setOpenCampaignId] = useState<string | null>(null)
  const [declineDraft, setDeclineDraft] = useState('')
  const [tab, setTab] = useState<'opps' | 'campaigns' | 'epk'>('opps')

  const listQ = useQuery<OpportunitiesPayload, ApiClientError>({
    queryKey: ['booking-opportunities', channel],
    queryFn: () => {
      const q =
        channel === 'all'
          ? '/booking-intelligence/opportunities'
          : `/booking-intelligence/opportunities?marketChannel=${channel}`
      return artistFetch<OpportunitiesPayload>(q)
    },
  })

  const channelsQ = useQuery<ChannelRow[], ApiClientError>({
    queryKey: ['booking-channels'],
    queryFn: () => artistFetch<ChannelRow[]>('/booking-intelligence/channels'),
  })

  const campaignsQ = useQuery<CampaignSummary[], ApiClientError>({
    queryKey: ['booking-campaigns'],
    queryFn: () => artistFetch<CampaignSummary[]>('/booking-intelligence/campaigns'),
  })

  const campaignDetailQ = useQuery<CampaignDetail, ApiClientError>({
    queryKey: ['booking-campaign', openCampaignId],
    queryFn: () =>
      artistFetch<CampaignDetail>(`/booking-intelligence/campaigns/${openCampaignId}`),
    enabled: !!openCampaignId,
  })

  const engagementQ = useQuery<EngagementRow[], ApiClientError>({
    queryKey: ['booking-epk-engagement'],
    queryFn: () =>
      artistFetch<EngagementRow[]>('/booking-intelligence/epk-engagement'),
  })

  const selected = useMemo(
    () => listQ.data?.items.find((i) => i.id === selectedId) ?? null,
    [listQ.data, selectedId],
  )

  async function createOpportunity() {
    if (!title.trim()) return
    setCreating(true)
    try {
      await artistFetch('/booking-intelligence/opportunities', {
        method: 'POST',
        body: { title: title.trim(), marketChannel: newChannel },
      })
      setTitle('')
      await qc.invalidateQueries({ queryKey: ['booking-opportunities'] })
      await qc.invalidateQueries({ queryKey: ['booking-channels'] })
    } catch (err) {
      alert(err instanceof ApiClientError ? err.message : 'Create failed')
    } finally {
      setCreating(false)
    }
  }

  async function createCampaign() {
    if (!campaignName.trim()) return
    setCreatingCampaign(true)
    try {
      const body: Record<string, unknown> = {
        name: campaignName.trim(),
        limit: 20,
        seedFromTopOpportunities: true,
      }
      if (channel !== 'all') body.marketChannel = channel
      const created = await artistFetch<CampaignDetail>(
        '/booking-intelligence/campaigns',
        { method: 'POST', body },
      )
      setCampaignName('')
      setOpenCampaignId(created.id)
      setTab('campaigns')
      await qc.invalidateQueries({ queryKey: ['booking-campaigns'] })
    } catch (err) {
      alert(err instanceof ApiClientError ? err.message : 'Campaign create failed')
    } finally {
      setCreatingCampaign(false)
    }
  }

  async function mintEpkLinks(campaignId: string) {
    try {
      await artistFetch(`/booking-intelligence/campaigns/${campaignId}/mint-epk-links`, {
        method: 'POST',
        body: {},
      })
      await qc.invalidateQueries({ queryKey: ['booking-campaign', campaignId] })
      await qc.invalidateQueries({ queryKey: ['booking-epk-engagement'] })
      await qc.invalidateQueries({ queryKey: ['booking-opportunities'] })
    } catch (err) {
      alert(err instanceof ApiClientError ? err.message : 'Mint failed')
    }
  }

  async function markLost(oppId: string) {
    try {
      await artistFetch(`/booking-intelligence/opportunities/${oppId}`, {
        method: 'PATCH',
        body: {
          status: 'lost',
          declineReason: declineDraft.trim() || null,
        },
      })
      setDeclineDraft('')
      await qc.invalidateQueries({ queryKey: ['booking-opportunities'] })
    } catch (err) {
      alert(err instanceof ApiClientError ? err.message : 'Update failed')
    }
  }

  return (
    <section
      className={cn(
        'space-y-3',
        compact ? '' : 'rounded-lg border border-slate-200 bg-white p-4',
      )}
    >
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-slate-900">Booking opportunities</h2>
          <p className="text-xs text-slate-500">
            Buyer/market lens — Top-20 campaigns &amp; EPK opens sit alongside territory &amp;
            pipeline.
          </p>
        </div>
        <div className="flex flex-wrap gap-1">
          {(['opps', 'campaigns', 'epk'] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={cn(
                'rounded px-2 py-1 text-xs capitalize',
                tab === t
                  ? 'bg-slate-900 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200',
              )}
            >
              {t === 'opps' ? 'Opportunities' : t === 'campaigns' ? 'Top-20' : 'EPK opens'}
            </button>
          ))}
        </div>
      </div>

      {tab === 'opps' && (
        <>
          <div className="flex flex-wrap gap-1">
            {CHANNELS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setChannel(c)}
                className={cn(
                  'rounded px-2 py-1 text-xs capitalize',
                  channel === c
                    ? 'bg-slate-900 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200',
                )}
              >
                {label(c)}
              </button>
            ))}
          </div>

          {!compact && channelsQ.data && (
            <div className="flex flex-wrap gap-2 text-xs text-slate-600">
              {channelsQ.data
                .filter((c) => c.opportunityCount > 0)
                .map((c) => (
                  <span key={c.marketChannel} className="rounded bg-slate-50 px-2 py-1">
                    {label(c.marketChannel)} → EPK {label(c.recommendedEpkVariant)} (
                    {c.opportunityCount})
                  </span>
                ))}
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <input
              className="min-w-[12rem] flex-1 rounded border border-slate-200 px-2 py-1.5 text-sm"
              placeholder="New opportunity title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <select
              className="rounded border border-slate-200 px-2 py-1.5 text-sm"
              value={newChannel}
              onChange={(e) => setNewChannel(e.target.value)}
            >
              {CHANNELS.filter((c) => c !== 'all').map((c) => (
                <option key={c} value={c}>
                  {label(c)}
                </option>
              ))}
            </select>
            <button
              type="button"
              disabled={creating || !title.trim()}
              onClick={() => void createOpportunity()}
              className="rounded bg-slate-900 px-3 py-1.5 text-sm text-white disabled:opacity-50"
            >
              Add
            </button>
          </div>

          <AsyncBoundary isLoading={listQ.isLoading} error={listQ.error}>
            <div className={cn('grid gap-3', selected ? 'lg:grid-cols-2' : '')}>
              <ul className="divide-y divide-slate-100 rounded border border-slate-100">
                {(listQ.data?.items ?? []).length === 0 && (
                  <li className="px-3 py-6 text-center text-sm text-slate-500">
                    No opportunities yet. Add a prospect by channel.
                  </li>
                )}
                {(listQ.data?.items ?? []).map((row) => (
                  <li key={row.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(row.id === selectedId ? null : row.id)}
                      className={cn(
                        'flex w-full items-start justify-between gap-3 px-3 py-2.5 text-left hover:bg-slate-50',
                        selectedId === row.id && 'bg-slate-50',
                      )}
                    >
                      <div>
                        <div className="text-sm font-medium text-slate-900">{row.title}</div>
                        <div className="text-xs text-slate-500">
                          {label(row.marketChannel)}
                          {row.buyerOrgName ? ` · ${row.buyerOrgName}` : ''}
                          {row.city ? ` · ${row.city}` : ''}
                          {row.epkLink ? ` · ${row.epkLink.views} views` : ''}
                        </div>
                      </div>
                      <div className="shrink-0 text-right text-xs">
                        <div className="font-medium text-slate-800">
                          Opp {row.opportunityScore ?? '—'} · Fit{' '}
                          {row.artistMarketFitScore ?? '—'}
                        </div>
                        <div className="text-slate-500">Grade {row.evidenceGrade ?? '—'}</div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>

              {selected && (
                <aside className="rounded border border-slate-200 bg-slate-50 p-3 text-sm">
                  <h3 className="font-semibold text-slate-900">Evidence</h3>
                  <p className="mt-1 text-xs text-slate-500">
                    Recommended EPK: {selected.recommendedEpkVariant ?? 'general'}
                    {selected.epkLink
                      ? ` · ${selected.epkLink.views} opens`
                      : ''}
                  </p>
                  <ul className="mt-3 space-y-2">
                    {(selected.evidence ?? []).map((e) => (
                      <li key={e.id} className="rounded bg-white px-2 py-1.5 text-xs">
                        <span className="font-medium">[{e.confidenceGrade}]</span> {e.claim}
                        <div className="text-slate-400">{e.source}</div>
                      </li>
                    ))}
                    {(selected.evidence ?? []).length === 0 && (
                      <li className="text-xs text-slate-500">No evidence cards yet.</li>
                    )}
                  </ul>
                  <div className="mt-3 space-y-2">
                    <label className="block text-xs text-slate-600">
                      Decline reason (when marking lost)
                      <input
                        className="mt-1 w-full rounded border border-slate-200 px-2 py-1 text-sm"
                        value={declineDraft}
                        onChange={(e) => setDeclineDraft(e.target.value)}
                        placeholder="Budget / timing / not a fit…"
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => void markLost(selected.id)}
                      className="rounded border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:bg-white"
                    >
                      Mark lost
                    </button>
                    {selected.declineReason && (
                      <p className="text-xs text-slate-500">
                        Prior reason: {selected.declineReason}
                      </p>
                    )}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2 text-xs">
                    <Link className="text-blue-700 underline" to="/pipeline">
                      Open pipeline
                    </Link>
                    <Link className="text-blue-700 underline" to="/territory">
                      Open territory
                    </Link>
                    <Link className="text-blue-700 underline" to="/settings/epks">
                      EPK settings
                    </Link>
                    {selected.inquiry && (
                      <span className="text-slate-600">
                        Linked inquiry · {selected.inquiry.stage}
                      </span>
                    )}
                  </div>
                </aside>
              )}
            </div>
          </AsyncBoundary>
        </>
      )}

      {tab === 'campaigns' && (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <input
              className="min-w-[12rem] flex-1 rounded border border-slate-200 px-2 py-1.5 text-sm"
              placeholder="Campaign name (seeds top 20 by score)"
              value={campaignName}
              onChange={(e) => setCampaignName(e.target.value)}
            />
            <button
              type="button"
              disabled={creatingCampaign || !campaignName.trim()}
              onClick={() => void createCampaign()}
              className="rounded bg-slate-900 px-3 py-1.5 text-sm text-white disabled:opacity-50"
            >
              Create Top-20
            </button>
          </div>
          <p className="text-xs text-slate-500">
            Uses the channel filter above when not “all”. Caps at 20 targets.
          </p>

          <AsyncBoundary isLoading={campaignsQ.isLoading} error={campaignsQ.error}>
            <ul className="divide-y divide-slate-100 rounded border border-slate-100">
              {(campaignsQ.data ?? []).length === 0 && (
                <li className="px-3 py-6 text-center text-sm text-slate-500">
                  No campaigns yet. Create one from your highest-scored opportunities.
                </li>
              )}
              {(campaignsQ.data ?? []).map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() =>
                      setOpenCampaignId(c.id === openCampaignId ? null : c.id)
                    }
                    className="flex w-full items-center justify-between px-3 py-2.5 text-left hover:bg-slate-50"
                  >
                    <div>
                      <div className="text-sm font-medium text-slate-900">{c.name}</div>
                      <div className="text-xs text-slate-500">
                        {c.status}
                        {c.marketChannel ? ` · ${label(c.marketChannel)}` : ''} ·{' '}
                        {c._count.targets} targets
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </AsyncBoundary>

          {openCampaignId && campaignDetailQ.data && (
            <div className="rounded border border-slate-200 bg-slate-50 p-3">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-sm font-semibold text-slate-900">
                  {campaignDetailQ.data.name}
                </h3>
                <button
                  type="button"
                  onClick={() => void mintEpkLinks(openCampaignId)}
                  className="rounded bg-slate-900 px-2 py-1 text-xs text-white"
                >
                  Mint EPK links
                </button>
              </div>
              <ol className="space-y-1.5 text-xs">
                {campaignDetailQ.data.targets.map((t) => (
                  <li
                    key={t.id}
                    className="flex flex-wrap items-start justify-between gap-2 rounded bg-white px-2 py-1.5"
                  >
                    <div>
                      <span className="font-medium text-slate-800">
                        #{t.rank} {t.opportunity.title}
                      </span>
                      <div className="text-slate-500">
                        {label(t.opportunity.marketChannel)} · Opp{' '}
                        {t.opportunity.opportunityScore ?? '—'} · {t.outreachStatus}
                        {t.epkLink ? ` · ${t.epkLink.views} views` : ''}
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      )}

      {tab === 'epk' && (
        <AsyncBoundary isLoading={engagementQ.isLoading} error={engagementQ.error}>
          <ul className="divide-y divide-slate-100 rounded border border-slate-100">
            {(engagementQ.data ?? []).length === 0 && (
              <li className="px-3 py-6 text-center text-sm text-slate-500">
                No campaign/opportunity EPK links yet. Create a Top-20 and mint links.
              </li>
            )}
            {(engagementQ.data ?? []).map((row) => (
              <li key={row.linkId} className="px-3 py-2.5 text-sm">
                <div className="font-medium text-slate-900">
                  {row.label ?? 'EPK link'}
                </div>
                <div className="text-xs text-slate-500">
                  {row.views} opens · {row.docxDownloads} docx downloads
                  {row.lastViewedAt
                    ? ` · last ${new Date(row.lastViewedAt).toLocaleString()}`
                    : ''}
                </div>
                {row.campaigns[0] && (
                  <div className="text-xs text-slate-400">
                    {row.campaigns[0].campaignName} → {row.campaigns[0].opportunityTitle}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </AsyncBoundary>
      )}
    </section>
  )
}
